import {
  type AbilityLevelState,
  type ActiveSources,
  abilityLevelStateUnder,
  activeProvides,
  cloakingCell,
  type FoldedStat,
  type FoldedUnit,
  type FoldedWeaponCells,
  foldUnit,
  foldWeapon,
  loadedExoticPrice,
  mountUnlocked,
  type ResearchSelection,
  withAbilityLevels,
} from "@sins2-atlas/compute";
import type {
  ExoticPrice,
  ProvidesLine,
  Race,
  StatCell,
  Unit,
  UnitAbility,
  UnitSquadrons,
  UnitWeapon,
} from "@sins2-atlas/game-data";
import { allocationAt, pointedAbilities } from "./ability-points";
import { dataset, itemsById } from "./dataset";
import { honouredPoints, type StatFrame } from "./frame";
import { BASE_LEVEL } from "./level";
import { equippedItems } from "./loadout";
import { activeResearchFor } from "./tech-set";

// The frame as the fold reads it (ADR 0009): the one place the frame pair becomes compute's
// active sources, and the memoised folds every surface reads its cells through. Split out of
// frame.ts on 2026-09-08 — that file keeps the URL contract and the frame type; this one is
// the only module in lib/ that imports @sins2-atlas/compute and the dataset for folding.

/** Where the reader's selection leaves one of a hull's abilities (Issue 51, ADR 0012;
 * Issue 54, ADR 0013; Issue 60, ADR 0015): the Ability Level in force, `unspent` where the
 * hull's Level permits the ability and no Ability Point is in it, what a locked one needs —
 * a hull level on a level-driven ability, a Research Subject on a research-levelled one — or
 * nothing at all on an ability no lever decides. The rule itself is compute's — the page asks
 * it rather than restating it, so what the ability list marks and what the stat rows folded
 * can never disagree. */
export function abilityLevel(
  ability: UnitAbility,
  frame: StatFrame,
  /** The OWNING hull. Its ability list is needed even to place one ability: `?points=` is
   * positional over the hull's level-driven abilities in the game's order, so which count
   * belongs to this ability is a fact about the whole list. Its shop listing is not — a
   * Loadout decides no ability's level (ADR 0012: an item GRANTS abilities, it levels
   * none). */
  unit: Pick<Unit, "race" | "abilities">,
): AbilityLevelState {
  return abilityLevelStateUnder(ability, activeSourcesOf(frame, { ...unit, items: [] }));
}

/** The stats whose cells the frame can move — compute's FOLDED_STATS, which since Issue 47
 * includes the three Price cells (a Loadout's items are bought). Everything else (supply,
 * build time, durability, the Corruption pool) is frame-invariant: no source touches it, so
 * the Base cell is true under every frame. The hangar and the Provides lines are NOT cells
 * and so are not in that list, but both do move with the Loadout (Issue 48) — read them
 * through frameSquadrons and frameProvides. */
export type FrameStat = FoldedStat;

/** The exotic components a hull and its Loadout jointly cost, merged by exotic type — the
 * list the entity page's Cost card renders, and exactly the list the efficiency cells
 * divided by, so the card cannot total a different set from the stats beneath it. Without a
 * Loadout it IS the hull's own `exoticPrice`. */
export function frameExoticPrice(unit: Unit, frame: StatFrame): readonly ExoticPrice[] {
  return loadedExoticPrice(unit.exoticPrice, activeSourcesOf(frame, unit));
}

// The site adds the bonuses up on the page (ADR 0009): nothing below looks a finished
// number up. Every surface — the Ships and Structures tables and their sort, the entity
// page's cards, the comparison's rows and best marks, the weapon table — reads its cells
// through frameCell / frameWeaponCells, which map the frame pair to the fold's active
// sources and fold the unit's tagged lists under the dataset's own ledger. A Tech Set
// (Issue 24) is one more research selection through the same two functions: it changes
// activeSourcesOf and frameKey, never the surfaces.

/** The research axis as the fold reads it, for one Race: no research on Base, all of it
 * under Fully-Teched, and a Tech Set projected onto this Race's keys. The projection is the
 * whole reason a Tech Set's Tier keys carry a Race — this Race's Tiers pass, every other
 * Race's are left out, and subject keys (which are race-unique) match only their own Race's
 * lists. Exported because the two per-hull levers ask the same question of the frame: an
 * item's level and a hull's Item Slot count are both functions of the research selection
 * (ADR 0012), and neither is a source the fold selects. */
export function researchSelectionOf(frame: StatFrame, race: Race): ResearchSelection {
  if (frame.tech !== undefined) return activeResearchFor(frame.tech, race);
  return frame.research === "fullyTeched" ? "all" : "none";
}

/** The frame pair as the fold's active sources: Base is no research source, Fully-Teched
 * every one the unit's lists carry (its Race's, by construction), and the Corruption Tier
 * rides beside either. */
export function activeSourcesOf(
  frame: StatFrame,
  /** The OWNING hull — its Race to project a Tech Set onto, its shop listing (`items`) for
   * the Loadout to be intersected with (a Loadout belongs to one hull, ADR 0012, while one
   * frame folds every unit a page shows), and its abilities, which the positional allocation
   * can only be read against. One value rather than three positional lists (review
   * 2026-09-10): a caller that held the hull was spelling the same three fields at every
   * site, and a caller that does not hold one says so by passing what it has (`{ race }`,
   * or `{ ...unit, items: [] }`) rather than by leaving a trailing default unfilled. */
  unit: Pick<Unit, "race"> & Partial<Pick<Unit, "items" | "abilities">>,
): ActiveSources {
  const research = researchSelectionOf(frame, unit.race);
  // An item's level comes from the research selection, never from a key of its own
  // (ADR 0012: the game levels items by research alone).
  const items = equippedItems(frame.loadout, unit.items ?? [], (id) => itemsById.get(id), research);
  const points = boughtAbilityLevels(unit.abilities ?? [], frame);
  return {
    research,
    ...(frame.corruption === "none" ? {} : { corruption: frame.corruption }),
    ...(items === undefined ? {} : { items }),
    // A Level is not intersected with anything: a hull with no ladder publishes no level table
    // and no level source, so it folds nothing under any level (compute's levelRow).
    ...(frame.level === undefined ? {} : { level: frame.level }),
    ...(points === undefined ? {} : { points }),
  };
}

/** The Ability Levels this hull's allocation has bought: ability id → the Ability Level, an
 * unspent ability absent. The URL's positional counts are read against the hull's own
 * level-driven abilities in the game's order, which is the only place that mapping happens.
 *
 * The allocation it reads is the one the page is SHOWING (lib/frame.ts, honouredPoints): an
 * allocation the game could not reach buys NOTHING — the whole thing falls back to nothing
 * spent, rather than part of it applying. That is ADR 0015 decision 2 held at the fold, and
 * it is decided in ONE place, so the trigger's count, the panel's positions, the notice and
 * the stat cells cannot read three different allocations. */
function boughtAbilityLevels(
  abilities: readonly UnitAbility[],
  frame: StatFrame,
): ReadonlyMap<string, number> | undefined {
  const points = honouredPoints({ abilities }, frame);
  if (points.length === 0) return undefined;
  const bought = new Map<string, number>();
  for (const [index, ability] of pointedAbilities(abilities).entries()) {
    const count = allocationAt(points, index);
    if (count > 0) bought.set(ability.id, count);
  }
  return bought.size === 0 ? undefined : bought;
}

/** The memo key of a frame — the fold is memoised per (unit, key), so a table of 166 rows
 * folds each unit once per frame, not once per cell per render. The Tech Set is part of the
 * key by its whole membership: two sets that differ by one tick must not share a fold.
 * `techUnknown` is deliberately absent — it moves no number. */
export function frameKey(frame: StatFrame): string {
  const tech = frame.tech === undefined ? "" : [...frame.tech].sort().join(",");
  // The Loadout joins the key by its whole membership, for the reason the Tech Set does:
  // two Loadouts differing by one item must not share a fold. The RESOLVED levels need not
  // be in it — they are a function of the research half, which already is.
  const loadout = frame.loadout === undefined ? "" : [...frame.loadout].sort().join(",");
  // The Level joins the key because it changes the BASE every other lever folds onto, so two
  // levels of one hull can never share a fold (Issue 49).
  // The allocation joins it for the reason the Level does: it changes which ability modifiers
  // fold, so two builds of one hull can never share a fold (ADR 0015).
  const points = frame.points === undefined ? "" : frame.points.join(",");
  return `${frame.research}/${frame.corruption}/${tech}/${loadout}/${frame.level ?? BASE_LEVEL}/${points}`;
}

const unitFolds = new WeakMap<Unit, Map<string, FoldedUnit>>();
const weaponFolds = new WeakMap<UnitWeapon, Map<string, FoldedWeaponCells>>();

function memoised<K extends object, V>(
  memo: WeakMap<K, Map<string, V>>,
  key: K,
  frame: StatFrame,
  compute: () => V,
): V {
  const byFrame = memo.get(key) ?? new Map<string, V>();
  memo.set(key, byFrame);
  const id = frameKey(frame);
  const cached = byFrame.get(id);
  if (cached !== undefined) return cached;
  const value = compute();
  byFrame.set(id, value);
  return value;
}

/** Every cell a unit renders under the frame pair, folded once and shared by every
 * surface that reads the unit under that frame. */
export function foldedUnit(unit: Unit, frame: StatFrame): FoldedUnit {
  return memoised(unitFolds, unit, frame, () =>
    foldUnit(unit, activeSourcesOf(frame, unit), dataset.verification),
  );
}

/** The cell a stat renders under the active frame pair. */
export function frameCell(unit: Unit, frame: StatFrame, stat: FrameStat): StatCell {
  return foldedUnit(unit, frame).cells[stat];
}

/** A battery's per-mount DPS / Nova Damage / Range cells under the active frame — the battery twin
 * of frameCell (Issue 19), folded from the battery's own lists. A battery of a pool-less unit
 * carries no Corruption source, so it reads the same under any tier. `unit` is the OWNING
 * hull: a battery has no Race, no shop listing and no abilities of its own, and the fold needs
 * all three — the Race to project a Tech Set onto, the shop for the Loadout's weapon-side
 * modifiers (ADR 0012), and the abilities so an ability-sourced modifier folds at the
 * Ability Level the allocation has bought, exactly as foldUnit resolves it
 * (withAbilityLevels; ADR 0015). The battery fold used to skip that last step, so its cells
 * and the unit's could disagree about a passive (review 2026-09-08). */
export function frameWeaponCells(
  weapon: UnitWeapon,
  frame: StatFrame,
  unit: Pick<Unit, "race" | "items" | "abilities">,
): FoldedWeaponCells {
  return memoised(weaponFolds, weapon, frame, () =>
    foldWeapon(
      weapon,
      withAbilityLevels(activeSourcesOf(frame, unit), unit.abilities),
      dataset.verification,
    ),
  );
}

/** This hull's Cloaking cell under the active frame (Issue 56, ADR 0013) — the first cell
 * on the site whose ADR 0003 state the reader's selection decides. The rule is compute's:
 * which level of the hull's cloak ability is in force, and the quality that level reaches,
 * or Not Applicable "cannot cloak" when none is. Both roster views fold under an empty
 * Loadout, so a hull whose cloak an item grants reads Not Applicable there, exactly as its
 * unfitted page does. */
export function frameCloaking(unit: Unit, frame: StatFrame): StatCell {
  return cloakingCell(unit, activeSourcesOf(frame, unit));
}

/** What this unit does for its owner under the active frame (Issue 48, ADR 0012). An
 * item-gated Provides line is stated exactly when the Loadout holds one of the items that
 * open it; every other line is unconditional. Every surface reads the group through here, so
 * the entity page, the comparison and the two roster tables cannot disagree about whether a
 * hull can build ships. The roster views declare no `?items=`, so they show the
 * unconditional lines and nothing else — which is what a list of bare hulls should say. */
export function frameProvides(unit: Unit, frame: StatFrame): readonly ProvidesLine[] {
  if (unit.provides.length === 0) return unit.provides;
  return activeProvides(unit.provides, activeSourcesOf(frame, unit));
}

/** The carrier's hangar under the active frame (Issue 48): a hangar Ship Item grows the
 * shared squadron capacity, so the craft counts follow the Loadout the way a stat cell does.
 * An Argonev reads 0 with nothing equipped and 14 with its three Auxiliary Hangars on.
 * Undefined on a hull with no carrier block, exactly as `unit.squadrons` is. */
export function frameSquadrons(unit: Unit, frame: StatFrame): UnitSquadrons | undefined {
  return foldedUnit(unit, frame).squadrons;
}

/** Whether a research-gated battery counts toward this unit's totals under the active
 * frame. The weapon table dims the row and explains it, and it must agree with the fold
 * exactly, so it asks the fold's own rule rather than restating it. */
export function weaponInTotals(weapon: UnitWeapon, frame: StatFrame, race: Race): boolean {
  return !weapon.itemGated && mountUnlocked(weapon, activeSourcesOf(frame, { race }));
}
